/**
 * SoleadLab 2.0 — Finding List Component
 *
 * B3.3: Minimal consumption path for finding entities from backend authority,
 * with finding workflow visible.
 *
 * Displays findings for the current site in a compact card list.
 * The status workflow (draft → confirmed → approved | rejected) is shown
 * as a color-coded badge on each card so the workflow is visible at a glance.
 *
 * Usage:
 *   <FindingList siteId="site-123" />
 *   <FindingList siteId="site-123" filters={{ status: 'confirmed' }} />
 *
 * The component sets the site scope on the finding store,
 * fetches items on mount, and renders each finding as a card
 * showing type, severity, status (workflow badge), title, and confidence.
 */

import { useEffect } from 'react';
import { useFindingStore } from '@/stores/findingStore';
import type { FindingFilters, FindingStatus } from '@/services/findingApi';

interface FindingListProps {
  siteId: string;
  filters?: FindingFilters;
}

// ─── Workflow badge config ───────────────────────────────────────────────────

const WORKFLOW_LABELS: Record<FindingStatus, string> = {
  draft: 'Draft',
  confirmed: 'Confirmed',
  approved: 'Approved',
  rejected: 'Rejected',
};

const SEVERITY_LABELS: Record<string, string> = {
  info: 'Info',
  warning: 'Warn',
  critical: 'Crit',
};

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDate(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function formatConfidence(c: number): string {
  return `${Math.round(c * 100)}%`;
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function FindingList({ siteId, filters }: FindingListProps) {
  const items = useFindingStore((s) => s.items);
  const loading = useFindingStore((s) => s.loading);
  const error = useFindingStore((s) => s.error);
  const setSiteId = useFindingStore((s) => s.setSiteId);
  const fetchItems = useFindingStore((s) => s.fetchItems);

  useEffect(() => {
    setSiteId(siteId);
  }, [siteId, setSiteId]);

  useEffect(() => {
    if (siteId) {
      fetchItems(filters);
    }
    // filters object identity changes on every render — stringify to stabilize
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, JSON.stringify(filters), fetchItems]);

  if (loading) {
    return <div className="finding-list__status">Loading findings...</div>;
  }

  if (error) {
    return <div className="finding-list__status finding-list__error">{error}</div>;
  }

  if (items.length === 0) {
    return <div className="finding-list__status">No findings found.</div>;
  }

  return (
    <div className="finding-list">
      <div className="finding-list__header">
        {items.length} finding{items.length !== 1 ? 's' : ''}
      </div>
      <div className="finding-list__items">
        {items.map((finding) => (
          <div key={finding.id} className="finding-list__card">
            {/* Workflow status badge — requirement: workflow visible */}
            <div
              className={`finding-list__workflow finding-list__workflow--${finding.status}`}
              title={`Workflow status: ${finding.status}`}
            >
              {WORKFLOW_LABELS[finding.status] ?? finding.status}
            </div>

            <div className="finding-list__card-type">
              {finding.findingType}
            </div>

            <div className="finding-list__card-meta">
              <span
                className={`finding-list__severity finding-list__severity--${finding.severity}`}
              >
                {SEVERITY_LABELS[finding.severity] ?? finding.severity}
              </span>
              <span className="finding-list__confidence" title="Confidence">
                {formatConfidence(finding.confidence)}
              </span>
            </div>

            <div className="finding-list__card-title" title={finding.title}>
              {finding.title}
            </div>

            {finding.createdAt && (
              <div className="finding-list__card-date">
                {formatDate(finding.createdAt)}
              </div>
            )}

            {finding.approvedBy && (
              <div className="finding-list__approved-by" title={`Approved by: ${finding.approvedBy}`}>
                ✓ {finding.approvedBy}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
